import type {
  CompositionSide,
  CompositionSideOut,
  CompositionUnit,
  Deployment,
  DeploymentEniOdb,
  EniOdbUnit,
  OrbatNode,
} from "../../api/types";
import { eniListRows } from "../../map/units/eniUnits";

/** Lignes des listes d'unités du dossier (docs/18 §6.1-6.2) : fonctions pures, testées par rows.test.ts. La source
 *  est la composition publiée par le serveur (`deployment.composition`) ; sur un serveur qui ne la publie pas encore,
 *  l'ordre de bataille ennemi du dossier (`deployment.eni_odb`) en tient lieu, sans ajout ni retrait. */

/** Camp de la composition prêt pour les listes. `legacy` : tiré de l'ordre de bataille ennemi (serveur antérieur) —
 *  rôles modifiables par `PUT /eni-roles`, aucun ajout ni retrait. */
export interface SideView {
  side: CompositionSide;
  out: CompositionSideOut;
  legacy: boolean;
  /** Racine du camp (force commandée, formation ennemie) quand elle n'est pas dans la liste des unités : le serveur
   *  ne publie que ses subordonnés. Symbole connu pour la force amie seulement. */
  root?: { label: string; sidc: string | null } | null;
}

/** Ligne d'une liste : unité, à glisser depuis la liste (`placeable`), glissable au temps affiché (`draggable`). */
export interface CompositionRow {
  unit: CompositionUnit;
  placeable: boolean;
  draggable: boolean;
}

/** Unité de l'ordre de bataille ennemi du dossier lue comme une unité de la composition. */
export function fromEniOdbUnit(unit: EniOdbUnit, placeable: boolean): CompositionUnit {
  return {
    id: unit.code,
    key: unit.key,
    code: unit.code,
    label: unit.display_name?.trim() || unit.code,
    side: "ENI",
    parent: unit.parent,
    depth: unit.depth,
    echelon: unit.echelon,
    sidc: unit.sidc,
    arm: unit.arm,
    specialty: unit.specialty,
    app6: null,
    function: null,
    origin: "odb",
    source_label: null,
    role: unit.role,
    role_default: unit.role_default,
    role_chosen: unit.role !== unit.role_default,
    engine_role: null,
    support: null,
    opo: null,
    placed: unit.placed,
    slot: unit.slot,
    placeable,
    removable: false,
  };
}

/** Camp tiré de l'ordre de bataille ennemi du dossier (serveur sans composition). */
export function legacyEniSide(odb: DeploymentEniOdb): SideView {
  const listed = new Map(eniListRows(odb).map((r) => [r.unit.code, r.placeable]));
  const units = odb.units.map((u) => fromEniOdbUnit(u, listed.get(u.code) ?? false));
  return {
    side: "ENI",
    legacy: true,
    out: {
      modified: units.some((u) => u.role_chosen),
      editable: false,
      refusal: null,
      units,
      removed: [],
      added_count: 0,
      roles_count: units.filter((u) => u.role_chosen).length,
      empty_slots: odb.unfilled,
      notes: [],
    },
  };
}

/** Camp d'un déploiement : composition du serveur, sinon l'ordre de bataille ennemi (ennemi seulement), sinon rien. */
export function sideView(deployment: Deployment | null | undefined, side: CompositionSide): SideView | null {
  const composition = deployment?.composition;
  if (composition) {
    const root =
      side === "AMI"
        ? deployment?.force_label
          ? { label: deployment.force_label, sidc: deployment.force_sidc ?? null }
          : null
        : composition.opo?.global
          ? { label: composition.opo.global, sidc: null }
          : null;
    return { side, out: side === "AMI" ? composition.ami : composition.eni, legacy: false, root };
  }
  if (side === "ENI" && deployment?.eni_odb) return legacyEniSide(deployment.eni_odb);
  return null;
}

/** Unité posée depuis la liste, hors des emplacements de la manœuvre : elle peut être retirée de la carte. */
export function offTemplate(unit: Pick<CompositionUnit, "placed" | "slot" | "depth">): boolean {
  return unit.depth > 0 && unit.placed && unit.slot === null;
}

/** Lignes d'un camp, dans l'ordre de l'arbre : unités sur la carte et unités à glisser (sous la racine). `onMap(key)` :
 *  l'unité est dessinée au temps affiché. `placeableOnly` : seulement celles à glisser (ordre de bataille de la carte,
 *  qui montre déjà les autres dans son arbre). */
export function compositionRows(
  view: SideView | null | undefined,
  onMap: (key: string) => boolean,
  placeableOnly = false,
): CompositionRow[] {
  if (!view) return [];
  const rows: CompositionRow[] = [];
  for (const unit of view.out.units) {
    if (unit.depth < 1 || !(unit.placed || unit.placeable)) continue;
    const placeable = unit.placeable && !unit.placed;
    const draggable = placeable || (offTemplate(unit) && !onMap(unit.key));
    if (placeableOnly && !draggable) continue;
    rows.push({ unit, placeable, draggable });
  }
  return rows;
}

/** Nombre d'unités à glisser sur la carte depuis la liste. */
export function placeableTotal(view: SideView | null | undefined): number {
  return compositionRows(view, () => true).filter((r) => r.placeable).length;
}

/** Unité de la composition par clé de déplacement (fiche d'unité, carte). */
export function unitByKey(view: SideView | null | undefined, key: string): CompositionUnit | undefined {
  return view?.out.units.find((u) => u.key === key);
}

/** Unité de la composition par identité. */
export function unitById(view: SideView | null | undefined, id: string): CompositionUnit | undefined {
  return view?.out.units.find((u) => u.id === id);
}

/** L'unité peut-elle être retirée du dossier : camp modifiable, ni la racine ni une unité propre à la mission. */
export function canRemove(view: SideView | null | undefined, unit: CompositionUnit): boolean {
  return Boolean(view && !view.legacy && view.out.editable && unit.depth > 0 && unit.removable);
}

/** Le rôle de l'unité peut-il être choisi : sous la racine, camp modifiable (ou ordre de bataille ennemi). */
export function canChooseRole(view: SideView | null | undefined, unit: CompositionUnit): boolean {
  return Boolean(view && unit.depth > 0 && (view.legacy || view.out.editable));
}

/** Résumé d'un camp modifié : unités ajoutées et retirées (noms), rôles choisis. */
export function compositionDigest(view: SideView | null | undefined): {
  added: string[];
  removed: string[];
  roles: number;
} {
  if (!view) return { added: [], removed: [], roles: 0 };
  return {
    added: view.out.units.filter((u) => u.origin === "added").map((u) => u.label),
    removed: view.out.removed.map((r) => r.label),
    roles: view.out.units.filter((u) => u.role_chosen).length,
  };
}

/** Arbre de l'ordre de bataille sans les unités retirées du dossier : elles ne paraissent qu'une fois, dans le volet
 *  « Retirées du dossier » (avec « Rétablir »), jamais en plus comme sous-unité « Référence » de leur parent. Le
 *  serveur ne les publie plus ; garde-fou pour un arbre encore en cache (`keepPreviousData`) ou un serveur antérieur.
 *  Une unité retirée part avec ses subordonnés, sauf ceux qui encadrent encore une unité du dossier (ils remontent à
 *  sa place) ; une unité ajoutée n'est jamais ôtée (son code peut reprendre celui d'une unité retirée). La racine
 *  reste. */
export function withoutRemoved(node: OrbatNode, view: SideView | null | undefined): OrbatNode {
  const children = withoutRemovedAll(node.children, view);
  return children === node.children ? node : { ...node, children };
}

/** `withoutRemoved` pour une liste de nœuds (racines de l'arbre ennemi) ; la liste reçue si rien n'est ôté. */
export function withoutRemovedAll(nodes: OrbatNode[], view: SideView | null | undefined): OrbatNode[] {
  const removed = view && !view.legacy ? view.out.removed : [];
  if (!view || !removed.length) return nodes;
  const gone = new Set(removed.flatMap((r) => [r.id, r.code]));
  const members = new Set(view.out.units.filter((u) => u.depth > 0).flatMap((u) => [u.id, u.key, u.code]));
  const isGone = (n: OrbatNode) => n.origin !== "added" && (gone.has(n.code) || Boolean(n.key && gone.has(n.key)));
  const isMember = (n: OrbatNode) => members.has(n.code) || Boolean(n.key && members.has(n.key));
  const holds = (n: OrbatNode): boolean => isMember(n) || n.children.some(holds);
  const same = (a: OrbatNode[], b: OrbatNode[]) => a.length === b.length && a.every((c, i) => c === b[i]);
  const prune = (n: OrbatNode): OrbatNode[] => {
    if (isGone(n)) return n.children.filter(holds).flatMap(prune);
    const children = n.children.flatMap(prune);
    return [same(children, n.children) ? n : { ...n, children }];
  };
  const out = nodes.flatMap(prune);
  return same(out, nodes) ? nodes : out;
}
